<template>
  <v-main class="test-page">
    <v-container fluid class="pa-4 pa-md-7">
      <header class="page-header">
        <div>
          <div class="overline primary--text font-weight-bold">ESP32 / MQTT</div>
          <h1 class="text-h4 font-weight-bold mb-1">灯控协议测试台</h1>
          <div class="grey--text text--darken-1">逐项测试、设备回包、人工确认</div>
        </div>
        <v-chip :color="tester.connectionColor.value" text-color="white" large><v-icon left small>{{ tester.connectionIcon.value }}</v-icon>{{ tester.connectionLabel.value }}</v-chip>
      </header>

      <v-row class="mt-4" align="start">
        <v-col cols="12" lg="3">
          <mqtt-connection-panel :config="config" :connected="tester.connected.value" :connecting="tester.connecting.value" :connection-error="tester.connectionError.value" @update-config="updateConfig" @connect="tester.connectMqtt" @disconnect="tester.disconnectMqtt" />

          <v-card class="progress-card mt-4" outlined>
            <v-card-title class="text-subtitle-1 font-weight-bold">测试进度<v-spacer /><span class="primary--text">{{ tester.completedCount.value }}/{{ tests.length }}</span></v-card-title>
            <v-card-text>
              <v-progress-linear :value="tester.progress.value" height="8" rounded color="success" />
              <div class="result-summary mt-4"><span class="success--text"><v-icon small color="success">mdi-check-circle</v-icon> 成功 {{ tester.passedCount.value }}</span><span class="error--text"><v-icon small color="error">mdi-close-circle</v-icon> 失败 {{ tester.failedCount.value }}</span></div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="9">
          <protocol-test-runner
            :tests="tests"
            :active-index="activeIndex"
            :connected="tester.connected.value"
            :clear-state="tester.clearState"
            :payload-preview="tester.payloadPreview"
            :format-json="tester.formatJson"
            :has-successful-response="tester.hasSuccessfulResponse"
            @change="changeTest"
            @previous="changeTest(activeIndex - 1)"
            @next="changeTest(activeIndex + 1)"
            @send="sendCurrentTest"
            @finish="finishCurrentTest"
          />
          <mqtt-log-panel class="mt-4" :logs="tester.logs.value" @clear="tester.clearLogs" />
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import MqttConnectionPanel from '@/components/MqttConnectionPanel.vue'
import MqttLogPanel from '@/components/MqttLogPanel.vue'
import ProtocolTestRunner from '@/components/ProtocolTestRunner.vue'
import { useMqttLightTester } from '@/composables/useMqttLightTester'
import { createProtocolTests } from '@/data/protocolTests'

export default {
  name: 'HomeView',
  components: { MqttConnectionPanel, MqttLogPanel, ProtocolTestRunner },
  setup () {
    const getMqttUrl = () => {
      const protocol = window.location.protocol;
      if (protocol === 'https:') {
        return 'wss://broker-cn.emqx.io:8084/mqtt';  // 加密连接
      } else {
        return 'ws://broker-cn.emqx.io:8083/mqtt';   // 非加密连接
      }
    };
    const config = reactive({
      brokerUrl: getMqttUrl(),
      commandTopic: 'sq/material/light',
      responseTopic: 'sq/material/light/response',
      statusTopic: 'sq/material/light/status',
      strip: 0,
      index: 3
    })
    const tests = reactive(createProtocolTests())
    const activeIndex = ref(0)
    const tester = useMqttLightTester(config, tests)

    const currentTest = () => tests[activeIndex.value]
    const updateConfig = (key, value) => { config[key] = value }
    const prepareCurrentTest = (nextIndex, previousIndex) => {
      const nextTest = tests[nextIndex]
      const previousTest = tests[previousIndex]
      if (previousTest && nextTest.preserveFrom === previousTest.key) {
        tester.preserveLights(`${previousTest.name} → ${nextTest.name}`)
        return
      }
      tester.clearAll(`进入第 ${nextIndex + 1} 项 ${nextTest.name}`)
    }
    const changeTest = nextIndex => {
      const normalizedIndex = Math.max(0, Math.min(tests.length - 1, Number(nextIndex)))
      if (normalizedIndex === activeIndex.value) return
      activeIndex.value = normalizedIndex
    }
    const sendCurrentTest = () => tester.sendTest(currentTest())
    const finishCurrentTest = result => {
      currentTest().confirmed = result
      if (!result) currentTest().error = '用户确认灯效异常'
      if (activeIndex.value < tests.length - 1) changeTest(activeIndex.value + 1)
      else tester.clearAll('全部测试完成')
    }
    const isEditableTarget = target => target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable || target.closest('button, a, [role="button"]'))
    const handleKeyboard = event => {
      if (isEditableTarget(event.target)) return
      if (event.key === 'Enter') {
        event.preventDefault()
        sendCurrentTest()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        changeTest(activeIndex.value - 1)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        changeTest(activeIndex.value + 1)
      }
    }

    watch(activeIndex, prepareCurrentTest)
    watch(tester.connected, value => { if (value) prepareCurrentTest(activeIndex.value, -1) })
    onMounted(() => window.addEventListener('keydown', handleKeyboard))
    onBeforeUnmount(() => window.removeEventListener('keydown', handleKeyboard))

    return { config, tests, activeIndex, tester, updateConfig, changeTest, sendCurrentTest, finishCurrentTest }
  }
}
</script>

<style scoped>
.test-page { background: #f4f6f8; min-height: 100vh; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.progress-card { border-color: #dfe3e8 !important; }
.result-summary { display: flex; justify-content: space-between; gap: 12px; font-size: .85rem; }
@media (max-width: 600px) { .page-header { flex-direction: column; } }
</style>
